import { Component, type ErrorInfo, type ReactNode } from "react";

/**
 * Catches a render error so one broken screen does not take the app with it.
 *
 * Without this, React unmounts the whole tree when anything throws while
 * rendering. The result is a blank page: no sidebar, no message, nothing in
 * the interface to say what happened or where. That is the hardest possible
 * thing to report and the hardest to act on, and it looks identical whether
 * the cause is a stale bundle, a field the API stopped sending, or a genuine
 * bug on one page.
 *
 * A class because this is the one thing hooks cannot do.
 */
export default class ErrorBoundary extends Component<
  { children: ReactNode },
  { failed: false } | { failed: true; message: string }
> {
  state: { failed: false } | { failed: true; message: string } = { failed: false };

  static getDerivedStateFromError(error: unknown) {
    return {
      failed: true as const,
      message: error instanceof Error ? error.message : String(error),
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Kept for whoever opens the console. The screen says what broke; this
    // says where, which is the half a person cannot read off the page.
    console.error("A screen failed to render.", error, info.componentStack);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="state state--failed" role="alert">
        <p>This screen could not be drawn.</p>
        <p className="state__detail">{this.state.message}</p>
        <button type="button" onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    );
  }
}
